import {UserPersona} from "@repo/guide-types";
import type {UserAttributes} from "@repo/shared-core";

import {
    isActivitiesPage,
    isUserDefaultActivityPage,
} from "./is-activities-page.ts";

/**
 * Get the user persona for the pathname and student emulation state.
 *
 * @param profile The current user's profile.
 * @param pathname The current pathname.
 * @param userEmulatingStudent Whether the user is emulating a student.
 * @returns `undefined` if the profile is nullish or the user does not have
 * access to the teacher AI guide; otherwise, `UserPersona.Teacher` if on
 * the activities page, or the teacher is not emulating a student;
 * otherwise, `UserPersona.Student`.
 */

export const maybeGetAsStudentPersona = (
    userAttributes: UserAttributes | null | undefined,
    pathname: string,
    userEmulatingStudent: boolean,
): UserPersona | null | undefined => {
    const isTeacherPersona = userAttributes?.hasAccessToAIGuideTeacher;
    const isParentPersona = userAttributes?.hasAccessToAIGuideParent;
    const isAdminPersona = userAttributes?.hasAccessToAIGuideDistrictAdmin;

    // Always return undefined for learner accounts.  The backend default is
    // for the persona field to be empty.
    if (!isTeacherPersona && !isParentPersona && !isAdminPersona) {
        return;
    }
    const currentPersona =
        pathname.includes("districts") && isAdminPersona
            ? UserPersona.DistrictAdmin
            : pathname.includes("parent")
              ? UserPersona.Parent
              : UserPersona.Teacher;
    // Users cannot emulate students on activities pages, except for on
    // the default user activity page.

    if (
        isActivitiesPage(pathname) &&
        !isUserDefaultActivityPage(pathname, currentPersona)
    ) {
        return currentPersona;
    }
    return userEmulatingStudent ? UserPersona.Student : currentPersona;
};
